import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Track refresh state to queue concurrent 401s
let isRefreshing = false;
let refreshQueue = [];

function processQueue(error, token) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

const credentialEndpoints = new Set(['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password', '/auth/refresh']);

function isPublicLink(url = '') {
  return ['/hep-view/', '/document-upload/', '/staff-info-public/', '/attorney-portal/', '/public/', '/abn/sign/'].some(prefix => url.startsWith(prefix));
}

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const session = localStorage.getItem('cervica_auth_session');
  if (Object.prototype.hasOwnProperty.call(config, '_authSession') && config._authSession !== session) {
    return Promise.reject(new Error('The signed-in account changed. Reload before trying again.'));
  }
  config._authSession = session;
  const token = localStorage.getItem('cervica_token');
  if (token && !isPublicLink(config.url)) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }
  return config;
});

// Handle 401 with refresh token rotation
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Explain an activation refusal without navigating away from unsaved work.
    const activationSession = localStorage.getItem('cervica_auth_session');
    if (originalRequest && activationSession && originalRequest._authSession === activationSession
      && !isPublicLink(originalRequest.url)
      && error.response?.status === 403
      && error.response?.data?.code === 'customer_activation_required') {
      window.dispatchEvent(new CustomEvent('cervica:activation-required', { detail: { session: activationSession } }));
    }

    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry || credentialEndpoints.has(originalRequest.url) || isPublicLink(originalRequest.url)
      || (originalRequest.url === '/auth/change-password' && error.response?.data?.error === 'Current password is incorrect')) {
      return Promise.reject(error);
    }

    if (originalRequest._authSession !== localStorage.getItem('cervica_auth_session')) return Promise.reject(error);

    const refreshToken = localStorage.getItem('cervica_refresh_token');
    if (!refreshToken) {
      clearAuth();
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    const session = originalRequest._authSession;
    if (isRefreshing) {
      // Queue this request until refresh completes
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((token) => {
        if (session !== localStorage.getItem('cervica_auth_session')) throw new Error('The signed-in account changed. Reload before trying again.');
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    isRefreshing = true;

    try {
      const { data } = await api.post('/auth/refresh', { refreshToken });
      if (session !== localStorage.getItem('cervica_auth_session')) throw new Error('The signed-in account changed. Reload before trying again.');
      if (typeof data?.token !== 'string' || !data.token.trim()
          || typeof data?.refreshToken !== 'string' || !data.refreshToken.trim()) {
        throw new Error('The session could not be refreshed. Please try again.');
      }
      localStorage.setItem('cervica_token', data.token);
      localStorage.setItem('cervica_refresh_token', data.refreshToken);
      originalRequest.headers.Authorization = `Bearer ${data.token}`;
      processQueue(null, data.token);
      return api(originalRequest);
    } catch (refreshError) {
      // Another tab may have rotated the token while our refresh was in
      // flight (rotation revokes the old token instantly). If localStorage
      // now holds a DIFFERENT refresh token than the one we sent, that tab
      // won the race — adopt its tokens instead of logging the whole
      // browser out (Ixchell 2026-08-12: repeated "Welcome Back" bounces
      // mid-note with schedule + notes tabs open).
      if (session !== localStorage.getItem('cervica_auth_session')) {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
      const currentRefresh = localStorage.getItem('cervica_refresh_token');
      if (currentRefresh && currentRefresh !== refreshToken) {
        const freshAccess = localStorage.getItem('cervica_token');
        processQueue(null, freshAccess);
        originalRequest.headers.Authorization = `Bearer ${freshAccess}`;
        return api(originalRequest);
      }
      processQueue(refreshError, null);
      // Only a real 401 means the session is dead. A 5xx or network
      // error (deploy flip, wifi blip) is transient — logging out on it
      // threw staff to the login page for no reason.
      if (refreshError.response?.status === 401) {
        clearAuth();
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

function clearAuth() {
  localStorage.setItem('cervica_auth_session', `${Date.now()}-${Math.random()}`);
  localStorage.removeItem('cervica_token');
  localStorage.removeItem('cervica_refresh_token');
  localStorage.removeItem('cervica_current_location');
  window.location.href = '/login';
}

export default api;
